import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DollarSign, Info, Loader2 } from 'lucide-react';
import { apiPost, apiPut } from '../../lib/api.js';
import { money, cardLabel } from './billingFormat.js';
import Toggle from './Toggle.jsx';

/**
 * "My Funds": balance, auto-recharge, and add funds by charging a saved card.
 * @param {{overview: object, onManageCards: () => void}} props
 */
export default function FundsPanel({ overview, onManageCards }) {
  const qc = useQueryClient();
  const cards = overview.cards || [];
  const defaultCard = cards.find((c) => c.isDefault) || cards[0];
  const [amount, setAmount] = useState('');
  const [cardId, setCardId] = useState('');
  const [agree, setAgree] = useState(false);
  const [manage, setManage] = useState(false);
  const [ar, setAr] = useState({ amount: overview.rechargeAmountUsd, threshold: overview.rechargeThresholdUsd });

  useEffect(() => { if (!cardId && defaultCard) setCardId(defaultCard.id); }, [defaultCard, cardId]);
  useEffect(() => { setAr({ amount: overview.rechargeAmountUsd, threshold: overview.rechargeThresholdUsd }); }, [overview.rechargeAmountUsd, overview.rechargeThresholdUsd]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['billing-overview'] });
  const amt = Number(amount);
  const amountOk = Number.isFinite(amt) && amt >= overview.minUsd && amt <= overview.maxUsd && Math.abs(amt * 100 - Math.round(amt * 100)) < 1e-6;
  const canAdd = amountOk && !!cardId && agree;

  const addFunds = useMutation({
    mutationFn: () => apiPost('/billing-center/funds', { amountUsd: amt, paymentMethodId: cardId, acceptTerms: true }),
    onSuccess: (r) => {
      toast.success(`Added ${money(r.amountUsd)} — new balance ${money(r.balanceUsd)}`);
      setAmount(''); setAgree(false);
      refresh();
      qc.invalidateQueries({ queryKey: ['billing-history'] });
    },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not add funds'),
  });
  const saveAr = useMutation({
    mutationFn: (body) => apiPut('/billing-center/auto-recharge', body),
    onSuccess: () => { toast.success('Auto-recharge updated'); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.error || 'Could not update auto-recharge'),
  });

  return (
    <section className="panel p-5" data-testid="funds-panel">
      <div className="flex items-start justify-between gap-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><DollarSign className="w-5 h-5 text-civix-purpleSoft" /> My Funds</h2>
        <div className="text-right text-sm">
          <div className="text-[11px] text-white/50">Funds Remaining</div>
          <div>Account balance: <span className="font-bold" data-testid="account-balance">{money(overview.balanceUsd)}</span></div>
        </div>
      </div>

      <p className="text-xs text-white/60 mt-3">Calls, texts, emails and AI usage are billed from your account balance at cost — no markup. Inbound calls and texts are free.</p>

      <div className="mt-4 flex items-center justify-between gap-3 border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 text-sm">
          <span>Auto-recharge my account balance:</span>
          <Toggle checked={overview.autoRecharge} disabled={saveAr.isPending} onChange={(v) => saveAr.mutate({ enabled: v })} label="Auto-recharge my account balance" />
        </div>
        <button type="button" onClick={() => setManage((m) => !m)} className="btn-ghost text-xs font-semibold">MANAGE</button>
      </div>
      {manage && (
        <form
          className="mt-3 grid sm:grid-cols-3 gap-3 items-end"
          onSubmit={(e) => { e.preventDefault(); saveAr.mutate({ amountUsd: Number(ar.amount), thresholdUsd: Number(ar.threshold) }); }}
        >
          <label className="text-xs text-white/60">Recharge amount (USD)
            <input type="number" min={overview.minUsd} max={overview.maxUsd} value={ar.amount} onChange={(e) => setAr({ ...ar, amount: e.target.value })} className="input mt-1" />
          </label>
          <label className="text-xs text-white/60">When balance falls below (USD)
            <input type="number" min="1" max="500" value={ar.threshold} onChange={(e) => setAr({ ...ar, threshold: e.target.value })} className="input mt-1" />
          </label>
          <button type="submit" disabled={saveAr.isPending} className="btn-primary text-sm disabled:opacity-50">Save</button>
        </form>
      )}

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <label className="text-[11px] text-white/50">Amount (USD)
          <div className="relative mt-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
            <input
              type="number" inputMode="decimal" min={overview.minUsd} max={overview.maxUsd} step="0.01"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              className="input pl-7 w-40" aria-label="Amount (USD)"
            />
          </div>
        </label>
        <div className="flex gap-1">
          {(overview.presetsUsd || []).map((p) => (
            <button key={p} type="button" onClick={() => setAmount(String(p))} className={`btn-primary text-sm px-4 ${Number(amount) === p ? 'ring-2 ring-white/60' : ''}`}>${p}</button>
          ))}
        </div>
      </div>
      {amount !== '' && !amountOk && (
        <p className="text-xs text-amber-300 mt-1">Enter between {money(overview.minUsd)} and {money(overview.maxUsd)}.</p>
      )}

      <div className="mt-3">
        {cards.length ? (
          <select value={cardId} onChange={(e) => setCardId(e.target.value)} className="input w-full" aria-label="Select payment method">
            {cards.map((c) => <option key={c.id} value={c.id}>{cardLabel(c)}{c.isDefault ? ' (default)' : ''}</option>)}
          </select>
        ) : (
          <div className="input text-white/50 text-sm flex items-center justify-between">
            No saved payment method
            <button type="button" onClick={onManageCards} className="text-civix-purpleSoft text-xs font-semibold">Add a card →</button>
          </div>
        )}
      </div>

      <div className="mt-3 flex items-start justify-between gap-4">
        <label className="flex items-start gap-2 text-xs text-white/70">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5" />
          <span>I agree to the <Link to="/terms" target="_blank" className="text-civix-purpleSoft hover:underline">Terms of Service</Link> and for the selected card to be charged {amountOk ? money(amt) : '$0'} USD. I understand there are no refunds for this purchase.</span>
        </label>
        <button type="button" disabled={!canAdd || addFunds.isPending} onClick={() => addFunds.mutate()} className="btn-primary text-sm shrink-0 disabled:opacity-40 flex items-center gap-2">
          {addFunds.isPending && <Loader2 className="w-4 h-4 animate-spin" />} ADD FUNDS
        </button>
      </div>

      {!overview.stripeConfigured && (
        <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-200 flex gap-2">
          <Info className="w-4 h-4 shrink-0" /> Payments are not configured on this server yet, so cards and funds are unavailable.
        </div>
      )}
    </section>
  );
}
